import { useState } from "react";
import {
    Button,
    Descriptions,
    Empty,
    Input,
    Modal,
    Typography,
    message,
} from "antd";
import { format, parseISO } from "date-fns";
import { getBrandAgreement, resetBrandAgreement } from "./brand-agreement.api";

const { Text } = Typography;

const REASON_MIN_LENGTH = 3;
const REASON_MAX_LENGTH = 500;

const formatAgreementDate = (d) => {
    const date = d ? parseISO(d) : null;
    return date && !isNaN(date.getTime())
        ? format(date, "dd MMM yyyy '@' h:mma")
        : "Never";
};

const BrandAgreementReset = ({ brandUserId }) => {
    const [email, setEmail] = useState("");
    const [searchedEmail, setSearchedEmail] = useState(null);
    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [resetting, setResetting] = useState(false);

    const trimmedReason = reason.trim();
    const reasonValid =
        trimmedReason.length >= REASON_MIN_LENGTH &&
        trimmedReason.length <= REASON_MAX_LENGTH;

    const fetchContact = async (value) => {
        setLoading(true);
        try {
            const res = await getBrandAgreement(brandUserId, value);
            if (res?.success) {
                setContact(res.result || null);
                setSearchedEmail(value);
            } else {
                message.error(res?.message || "Failed to look up contact.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        const v = (value || "").trim();
        if (!v) {
            return;
        }
        fetchContact(v);
    };

    const closeModal = () => {
        setModalOpen(false);
        setReason("");
    };

    const handleReset = async () => {
        if (!reasonValid || !contact) {
            return;
        }
        setResetting(true);
        try {
            const res = await resetBrandAgreement(
                brandUserId,
                contact.accountId,
                trimmedReason,
            );
            if (res?.success) {
                message.success("Agreement reset");
                closeModal();
                await fetchContact(searchedEmail);
            } else {
                message.error(res?.message || "Failed to reset agreement.");
            }
        } finally {
            setResetting(false);
        }
    };

    return (
        <div style={{ maxWidth: 640 }}>
            <Input.Search
                allowClear
                placeholder="Contact email"
                enterButton="Search"
                value={email}
                onChange={(e) => setEmail(e?.target?.value || "")}
                onSearch={handleSearch}
                loading={loading}
                style={{ marginBottom: 16 }}
            />

            {searchedEmail !== null && !contact && !loading && (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No contact found with that email for this brand."
                />
            )}

            {contact && (
                <>
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="Name">
                            {contact.name}
                        </Descriptions.Item>
                        <Descriptions.Item label="Email">
                            {contact.email}
                        </Descriptions.Item>
                        <Descriptions.Item label="Agreed to terms">
                            {formatAgreementDate(contact.termsAgreementDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label="eSign Completed">
                            {contact.termsAgreementESigned ? "Yes" : "No"}
                        </Descriptions.Item>
                    </Descriptions>
                    {contact.termsAgreementDate && (
                        <Button
                            danger
                            style={{ marginTop: 16 }}
                            onClick={() => setModalOpen(true)}>
                            Reset agreement
                        </Button>
                    )}
                </>
            )}

            <Modal
                title="Reset brand agreement"
                open={modalOpen}
                onOk={handleReset}
                onCancel={closeModal}
                confirmLoading={resetting}
                okText="Reset agreement"
                okButtonProps={{ danger: true, disabled: !reasonValid }}
                destroyOnClose>
                <p>
                    Reset the brand agreement for{" "}
                    <Text strong>{contact?.email}</Text>?
                </p>
                <label htmlFor="brand-agreement-reset-reason">
                    <Text strong>Reason</Text>
                </label>
                <Input.TextArea
                    id="brand-agreement-reset-reason"
                    rows={3}
                    value={reason}
                    maxLength={REASON_MAX_LENGTH}
                    showCount
                    onChange={(e) => setReason(e?.target?.value || "")}
                />
                <Text
                    type="secondary"
                    style={{ display: "block", marginTop: 8 }}>
                    The brand sees this reason on the contact's timeline. The
                    ambassador is asked to agree again on their next portal
                    visit.
                </Text>
            </Modal>
        </div>
    );
};

export default BrandAgreementReset;
